/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Mail, Smartphone, Database, Clock } from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import PageMeta from "@/components/PageMeta";

const WEB_ACCOUNT_SETTINGS_URL = "https://web.shopbook.tech/settings";

const STEPS = [
  {
    n: "01",
    icon: Smartphone,
    title: "Delete from Shopbook Settings",
    body: (
      <>
        On mobile, open <strong>More → Settings → Delete account</strong>. On the web app, open <strong>Settings → Delete account</strong>. Shopbook asks you to type the confirmation phrase before processing the request. All sessions are revoked, notification delivery is disabled, and the personal account identity is anonymised.
      </>
    ),
  },
  {
    n: "02",
    icon: Mail,
    title: "Or delete securely on the web",
    body: (
      <>
        Open <a href={WEB_ACCOUNT_SETTINGS_URL} className="text-[#0E6B6B] underline">Shopbook web Settings</a>, sign in securely if prompted, and choose <strong>Delete account</strong>. This works without reinstalling the mobile app and uses the same verified deletion process.
      </>
    ),
  },
];

const DELETED = [
  "Personal account contact details (email and phone)",
  "Your personal staff identity and access to the workspace",
  "Active authentication sessions, PIN credentials and outstanding OTP challenges",
  "Push-notification access and active device subscriptions",
];

const RETAINED = [
  { label: "Anonymised aggregate analytics", body: "Used to understand product usage, with no link to you or your business." },
  { label: "Financial and billing records", body: "Retained only for the period required by applicable tax, accounting, fraud-prevention, or audit obligations." },
  { label: "Organization workspace records", body: "Products, sales, inventory and other organization records may need to remain available to other authorized workspace members and may be retained where required for financial, tax, fraud-prevention, security, or audit obligations. Personal access and identity are removed." },
  { label: "Records required by law", body: "If a legal authority requires us to retain specific data, we keep only what's strictly necessary." },
];

export default function AccountDeletion() {
  return (
    <>
      <PageMeta title="Account Deletion" description="How to delete a Shopbook account from web or mobile, request verified deletion by email, and understand retained records." />
      <PageHero
        eyebrow="Account & data"
        title="Delete your Shopbook account."
        italicTail=""
        intro="You can start account deletion from Settings on web or mobile. Access is revoked when the request is processed, and only records with a valid legal or security reason are retained."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-12 space-y-14">
          <div className="rounded-3xl bg-[#FFF6EE] border border-[#D96C4A]/20 p-6 md:p-7 flex items-start gap-4">
            <AlertTriangle className="h-6 w-6 text-[#D96C4A] flex-none mt-0.5" />
            <div>
              <h2 className="font-display text-lg font-semibold text-[#0A2424]">Account deletion is more than signing out</h2>
              <p className="mt-1 text-base text-[#3a4a4a]">
                The owner-level action revokes your access and anonymises your personal account; it is not the same as signing out of one device. If you only want to end the current session, choose <strong>Logout</strong> instead. Contact <a href="mailto:privacy@shopbook.tech" className="text-[#0E6B6B] underline">privacy@shopbook.tech</a> if you also need legally retained organization records reviewed.
              </p>
            </div>
          </div>

          {/* Steps */}
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A2424]">How to delete your account</h2>
            <div className="mt-6 space-y-5">
              {STEPS.map((s) => (
                <article
                  key={s.n}
                  data-testid={`delete-step-${s.n}`}
                  className="rounded-3xl bg-[#F7FAF9] p-7 border border-[#0E6B6B]/10 flex gap-5"
                >
                  <div className="h-12 w-12 rounded-2xl bg-[#0E6B6B] text-white flex items-center justify-center font-display font-semibold text-base flex-none">
                    {s.n}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className="h-4 w-4 text-[#0E6B6B]" />
                      <h3 className="font-display text-lg font-semibold text-[#0A2424]">{s.title}</h3>
                    </div>
                    <p className="text-base text-[#3a4a4a] leading-relaxed">{s.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* What gets deleted */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Database className="h-5 w-5 text-[#0E6B6B]" />
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A2424]">What gets deleted</h2>
            </div>
            <p className="text-base text-[#3a4a4a]">
              When the in-app deletion is processed, the following are removed, disabled, or irreversibly anonymised:
            </p>
            <ul className="mt-5 space-y-3">
              {DELETED.map((d) => (
                <li key={d} className="flex items-start gap-3 text-base text-[#1c2c2c]">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D96C4A] flex-none" />
                  {d}
                </li>
              ))}
            </ul>
          </div>

          {/* What's retained */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-[#0E6B6B]" />
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#0A2424]">What we may retain (and why)</h2>
            </div>
            <p className="text-base text-[#3a4a4a]">
              By law, a small set of records may be kept for a limited time, even after account deletion:
            </p>
            <div className="mt-5 space-y-3">
              {RETAINED.map((r) => (
                <div key={r.label} className="rounded-2xl bg-white border border-[#0E6B6B]/10 p-5">
                  <div className="font-semibold text-[#0A2424]">{r.label}</div>
                  <div className="text-sm text-[#4B5E5E] mt-1">{r.body}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl bg-[#0A3B3B] text-white p-7 md:p-10">
            <h2 className="font-display text-xl md:text-2xl font-semibold">Need help with deletion?</h2>
            <p className="mt-2 text-base text-white/80 max-w-xl">
              If you no longer have the mobile app, use the secure web deletion flow. For accessibility or account-recovery help, contact <a href="mailto:privacy@shopbook.tech" className="text-[#FFD8A8] underline">privacy@shopbook.tech</a>.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={WEB_ACCOUNT_SETTINGS_URL}
                className="inline-flex items-center rounded-full bg-[#FFD8A8] text-[#0A2424] px-6 py-3 text-base font-semibold hover:bg-white transition-colors"
              >
                Open web Settings
              </a>
              <Link
                to="/privacy-policy"
                className="inline-flex items-center rounded-full border border-white/30 px-6 py-3 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Read Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
